import { cn } from '@/shared/utilities';
import { Close, PlayArrow } from '@mui/icons-material';
import { IconButton, Modal, Skeleton } from '@mui/material';
import React, { useState } from 'react';
import { Blurhash } from 'react-blurhash';
import ReactPlayer from 'react-player';
import { useAttachmentUrl } from '../../queries/use-attachment-url';

interface VideoViewProps {
  path: string;
  alt: string;
  thumbnailPath?: Nullable<string>;
  blurHash?: Nullable<string>;
  width?: number;
  height?: number;
  className?: string;
  isGallery?: boolean;
}

const VideoView: React.FC<VideoViewProps> = React.memo(
  ({ path, thumbnailPath, blurHash, alt, className = '', width = '100%', height = '100%' }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { blobUrl: imageUrl, isError: hasError, isLoading } = useAttachmentUrl(thumbnailPath || '');
    const { blobUrl: videoUrl } = useAttachmentUrl(path, isModalOpen);

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    return (
      <>
        <div className={cn('mex:relative mex:h-full mex:w-full', className)} style={{ width, height }}>
          {/* Loading placeholder: Blurhash under Skeleton if available, else just Skeleton */}
          {isLoading && !hasError && (
            <div className='mex:absolute mex:inset-0'>
              {blurHash && (
                <Blurhash
                  hash={blurHash}
                  width='100%'
                  height='100%'
                  resolutionX={32}
                  resolutionY={32}
                  punch={1}
                  className='mex:absolute mex:inset-0 mex:h-full mex:w-full mex:object-cover'
                />
              )}
              <Skeleton
                variant='rectangular'
                width='100%'
                height='100%'
                className={blurHash ? 'mex:absolute mex:inset-0' : 'mex:block mex:h-full mex:w-full'}
                sx={{ background: blurHash ? 'transparent' : undefined }}
              />
            </div>
          )}
          {/* Error state with ARIA for accessibility */}
          {hasError && (
            <div
              className='mex:absolute mex:inset-0 mex:flex mex:h-full mex:w-full mex:items-center mex:justify-center mex:bg-gray-100'
              role='img'
              aria-label='Không thể tải ảnh'
            >
              <span className='mex:text-gray-500 mex:text-sm'>Không thể tải ảnh</span>
            </div>
          )}
          <div className='mex:!flex mex:absolute mex:inset-0 mex:items-center mex:justify-center mex:bg-opacity-30'>
            <IconButton
              color='inherit'
              className='mex:!bg-black/30 mex:p-2'
              onClick={openModal}
              aria-label='Phát video'
            >
              <PlayArrow fontSize='large' className={cn('mex:!text-white mex:transition-all mex:duration-200')} />
            </IconButton>
          </div>
          {/* Actual image with lazy loading */}
          {imageUrl && !hasError && (
            <img
              src={imageUrl}
              alt={alt || 'Hình ảnh đính kèm'}
              loading='lazy'
              className={cn(
                'mex:h-full mex:w-full mex:min-w-[100px] mex:object-cover mex:duration-300',
                isLoading ? 'mex:opacity-0' : 'mex:opacity-100'
              )}
            />
          )}
        </div>

        {/* Modal for Video Player */}
        <Modal open={isModalOpen} onClose={closeModal}>
          <div className='mex:fixed mex:inset-0 mex:z-50 mex:flex mex:items-center mex:justify-center mex:bg-black/50 mex:p-4'>
            <div className='mex:absolute mex:top-5 mex:right-5 mex:justify-end mex:border-b mex:p-4'>
              <IconButton onClick={closeModal} aria-label='Đóng modal' size='large' className='mex:!text-white'>
                <Close />
              </IconButton>
            </div>
            <div className='mex:flex mex:flex-1 mex:items-center mex:justify-center mex:p-4'>
              {videoUrl && (
                <div className='mex:h-[80vh] mex:max-w-[90vw]'>
                  <ReactPlayer src={videoUrl} controls width='100%' height='100%' />
                </div>
              )}
            </div>
          </div>
        </Modal>
      </>
    );
  }
);

export default VideoView;
